import Guid from "_shared/guids/guid";
import ProposalField from "work/proposals/proposal-fields/proposal-field";
import type {
  ProposalActivity,
  ProposalCollaborationEnvelope,
  ProposalNegotiationEventKind,
} from "work/proposals/negotiation/proposal-collaboration-contract";
import {
  proposalFieldForTimelinePath,
  proposalTimelineItemPathForField,
  proposalTimelinePathForField,
} from "work/proposals/negotiation/proposal-field-timeline-path";

export type ProposalFieldUnread = {
  /** Content changes on the field's path, authored by someone other than the viewer, past the seen cursor. */
  unreadCount: number;
  /** Highest stream sequence among those unread entries (0 when none) — the value to mark seen through. */
  latestUnseenSequence: number;
};

const noProposalFieldUnread: ProposalFieldUnread = { unreadCount: 0, latestUnseenSequence: 0 };

/**
 * The negotiation event kinds the change history treats as a content change (a scalar field set or a
 * collection add/update/remove). Comment, resolution-decision and reviewer/lifecycle kinds are excluded,
 * so a comment or a decision never inflates a field's change-notification count.
 */
export const proposalContentActivityKinds: ReadonlySet<ProposalNegotiationEventKind> = new Set([
  "FieldSet",
  "CollectionItemAdded",
  "CollectionItemUpdated",
  "CollectionItemRemoved",
]);

export type ProposalUnreadEnvelope = Pick<
  ProposalCollaborationEnvelope,
  | "recentActivity"
  | "viewerSeenThroughStreamVersion"
  | "viewerSeenAllThroughStreamVersion"
  | "viewingParty"
  | "baselineSequence"
>;

/**
 * The stream version the viewer has already seen for a path: the per-path cursor when present, otherwise
 * the whole-proposal cursor, otherwise 0 — floored by `baselineSequence` (the counterparty's
 * first-submission sequence) when present, so a stale or never-persisted client cursor can never resurface
 * the counterparty's first-receipt content as an unread change. Mirrors the backend fallback so a
 * never-marked path derives the same baseline the server would.
 */
export function proposalSeenCursorForPath(
  envelope: Pick<ProposalUnreadEnvelope, "viewerSeenThroughStreamVersion" | "viewerSeenAllThroughStreamVersion" | "baselineSequence">,
  path: string,
  /**
   * The item thread's own key, for a collection whose items keep their own history — today a
   * fee-schedule category (see `proposalTimelineItemPathForField`).
   *
   * It can only ever move the cursor FORWARD of whatever the field-level chain below already says.
   * Acknowledging the field is a statement about everything under it, so it still clears every item;
   * acknowledging one item says nothing about its siblings, so it must not drag the others back.
   * Reading it as a plain first-in-chain fallback would do exactly that — a category acknowledged
   * before a wholesale acknowledgement would resurface changes the field-level cursor had cleared.
   */
  itemPath?: string,
): number {
  const perItem = itemPath === undefined ? undefined : envelope.viewerSeenThroughStreamVersion?.[itemPath];
  const perPath = envelope.viewerSeenThroughStreamVersion?.[path];
  // A field's changes land on its DESCENDANT paths ("/feeSchedule/categories"), while everything that
  // marks a field seen writes the cursor at the field's own path ("/feeSchedule") — the history
  // control and the change traverser both address a field by that canonical path. Falling back to it
  // is what lets acknowledging Fee Schedule actually clear a fee-category change.
  const fieldPath = canonicalFieldPathFor(path);
  const fieldCursor =
    fieldPath !== undefined && fieldPath !== path
      ? envelope.viewerSeenThroughStreamVersion?.[fieldPath]
      : undefined;
  const fieldLevelCursor =
    typeof perPath === "number"
      ? perPath
      : typeof fieldCursor === "number"
        ? fieldCursor
        : (envelope.viewerSeenAllThroughStreamVersion ?? 0);
  const cursor = typeof perItem === "number" ? Math.max(perItem, fieldLevelCursor) : fieldLevelCursor;
  return Math.max(cursor, envelope.baselineSequence ?? 0);
}

/** The canonical path of the field a semantic path belongs to, e.g. "/feeSchedule/categories" → "/feeSchedule". */
function canonicalFieldPathFor(path: string): string | undefined {
  return proposalTimelinePathForField(proposalFieldForTimelinePath(path));
}

/**
 * Derives a field's unread change-notification count from the collaboration envelope — no per-field
 * network call. An entry counts when it is a content change on the field's path, sits past the viewer's
 * seen cursor for that path, and was not authored by the viewer themselves — a same-party teammate's or
 * reviewer's save counts too, since noticing a teammate just changed something is exactly the point of
 * this badge.
 */
export function proposalFieldUnread(
  envelope: ProposalUnreadEnvelope | undefined,
  field: ProposalField | null | undefined,
  currentUserId: string | undefined,
): ProposalFieldUnread {
  const path = proposalTimelinePathForField(field ?? undefined);
  if (!envelope || !path || !field) return noProposalFieldUnread;
  // Counted against the field that OWNS the path, never against `field` as the caller spelled it. The
  // two differ for a control scoped to one item of a collection: a team member's row addresses its
  // field as `TeamMember(userId)` and a fee category's row as `FeeScheduleCategory(categoryId)`, while
  // every activity resolves back to the id-less field that owns its path, and `isEqualTo` refuses a
  // match when only one side carries an id. Those controls counted zero — and zero is not merely a
  // missing badge, because the "mark seen" request sits behind an `unreadCount > 0` guard, so the field
  // could not be acknowledged from an item row at all. That is the only way left to acknowledge the
  // team since #4954 took the whole-roster history control off the list, and the fee schedule since
  // #4951 did the same to it.
  //
  // Field granularity is also the only granularity the cursor can keep its promise at: it is written
  // per PATH, so every item row of one collection necessarily reads the same count and clears the same
  // changes — which is exactly what opening any one of them does.
  const countedField = proposalFieldForTimelinePath(path);
  if (!countedField) return noProposalFieldUnread;

  // ...except for a collection whose items keep their own history. A fee-schedule category is counted
  // against the category (#5118): its row's badge is about that row, so every category showing the
  // schedule's whole count — and opening any one of them clearing all of them — was the aggregate the
  // fee schedule is not supposed to have. The item id is the discriminator the stream already carries,
  // so nothing about the path changes; only what is counted within it.
  const itemId = field.isFeeScheduleCategory ? field.id?.value.toLowerCase() : undefined;
  const itemPath = proposalTimelineItemPathForField(field);

  let unreadCount = 0;
  let latestUnseenSequence = 0;
  for (const activity of envelope.recentActivity) {
    if (!activity.path) continue;
    // Excluded only when the activity can be positively attributed to a DIFFERENT item. An activity
    // carrying no item id belongs to no category in particular, and the id-less schedule has had no
    // control of its own since #4951 — so dropping it from every category's count would be the one way
    // to make a change nobody can see. It stays on all of them, as it was before.
    // Compared case-insensitively: the stream and the projection have not always agreed on casing, the
    // same reason the timeline panel lower-cases before scoping entries to an item.
    const activityItemId = activity.itemId?.toLowerCase();
    if (itemId !== undefined && activityItemId !== undefined && activityItemId !== itemId) continue;
    const cursor = proposalSeenCursorForPath(envelope, activity.path, itemPath);
    if (!isUnseenChangeByOthers(activity, countedField, cursor, currentUserId)) continue;
    unreadCount += 1;
    if (activity.sequence > latestUnseenSequence) latestUnseenSequence = activity.sequence;
  }
  return { unreadCount, latestUnseenSequence };
}

/**
 * The distinct proposal fields carrying an unread change, derived from the same per-path cursor + filter
 * as {@link proposalFieldUnread}. The change traverser ("Next / Prev Change") uses this so it stops at a
 * field whose only pending item is an unread negotiation change, not just fields with an issue or an
 * unread comment.
 */
export function proposalUnreadChangeFields(
  envelope: ProposalUnreadEnvelope | undefined,
  currentUserId: string | undefined,
): ProposalField[] {
  if (!envelope) return [];

  const fields: ProposalField[] = [];
  const seenFieldKeys = new Set<string>();
  for (const activity of envelope.recentActivity) {
    const path = activity.path;
    if (!path) continue;
    const field = proposalFieldForTimelinePath(path);
    if (!field) continue;
    // Dedupe by the STOP, not by path: a fee-schedule edit and a fee-template change resolve to the
    // same Fee Schedule field, and the traverser must stop there once, not once per underlying path.
    const stop = unreadChangeStopField(field, activity.itemId);
    if (seenFieldKeys.has(stop.cacheKey)) continue;
    // Membership is still decided against the field that owns the path — the stop is where the reader
    // is sent, not what the activity belongs to.
    const cursor = proposalSeenCursorForPath(envelope, path, proposalTimelineItemPathForField(stop));
    if (!isUnseenChangeByOthers(activity, field, cursor, currentUserId)) continue;
    seenFieldKeys.add(stop.cacheKey);
    fields.push(stop);
  }
  return fields;
}

/**
 * Where "Next Change" should stop for an activity — its field, or, for a fee-schedule category, the
 * category itself (#5118).
 *
 * The schedule was one stop however many categories had changed, and the reader arriving there had to
 * find them by eye in the widest table on the page. A category holds its own history, so it is its own
 * stop, and the history that opens on arrival is that category's.
 *
 * An id that is not a well-formed Guid falls back to the field. The stream's invariants require an item
 * id on a collection event, so this is about a malformed one rather than a missing one, and a stop one
 * level too coarse is a far better answer than a throw inside a render.
 */
function unreadChangeStopField(field: ProposalField, itemId: string | null | undefined): ProposalField {
  if (!itemId || !field.isEqualTo(ProposalField.FeeSchedule) || !Guid.isValid(itemId)) return field;
  return ProposalField.FeeScheduleCategory(new Guid(itemId));
}

/**
 * Whether an activity entry is a content change belonging to `field`, past `cursor`, and NOT authored by
 * the viewer. Deliberately does not consider `party`: a teammate or reviewer on the viewer's own side
 * saving a change is precisely what this badge exists to surface, not outgoing work to suppress. Only
 * the viewer's own edit is excluded.
 *
 * Membership is decided by resolving the activity's path back to its field — the same mapping the
 * change traverser uses — rather than by string-equality against the field's canonical path. Those two
 * are not the same question for any collection-rooted field: a fee-category edit lands on
 * "/feeSchedule/categories" while Fee Schedule's own path is "/feeSchedule", so an equality test
 * reported ZERO unread changes on Fee Schedule, Policies, Conflicts, Team and Discount while the
 * traverser and the tab badge — which already resolved by field — counted them. The badge never lit,
 * opening the history never marked anything seen, and the tab's count could not be cleared.
 */
function isUnseenChangeByOthers(
  activity: ProposalActivity,
  field: ProposalField,
  cursor: number,
  currentUserId: string | undefined,
): boolean {
  if (activity.sequence <= cursor) return false;
  if (!proposalContentActivityKinds.has(activity.kind)) return false;
  if (activity.actorUserId === currentUserId) return false;
  const activityField = proposalFieldForTimelinePath(activity.path);
  return activityField !== undefined && activityField.isEqualTo(field);
}
